/**
 * +patrols: `?debug=patrols` overlay — waypoint paths (gold, closed for loops), wander circles
 * (teal) and loose/linked pack rings (blue/red) drawn just above the floor. Debug only.
 */
import * as THREE from 'three';
import type { DungeonDef, Vec2 } from '@keystone/sim';

/** True when the page was opened with `?debug=patrols` (or a list containing it). */
export function patrolsRequested(search: string): boolean {
  const v = new URLSearchParams(search).get('debug');
  return !!v && v.split(',').includes('patrols');
}

export function buildPatrolOverlay(d: DungeonDef, floorAt: (p: Vec2) => number, owned: { dispose(): void }[]): THREE.Group {
  const g = new THREE.Group();
  g.name = 'patrol-overlay';
  const line = (pts: Vec2[], color: number, closed: boolean): void => {
    const v = pts.map((p) => new THREE.Vector3(p.x, floorAt(p) + 0.15, -p.y));
    const geo = new THREE.BufferGeometry().setFromPoints(closed ? [...v, v[0]!] : v);
    const mat = new THREE.LineBasicMaterial({ color, depthTest: false, transparent: true, opacity: 0.9 });
    owned.push(geo, mat);
    const l = new THREE.Line(geo, mat);
    l.renderOrder = 999;
    g.add(l);
  };
  const circle = (c: Vec2, r: number, color: number): void => line(Array.from({ length: 24 }, (_, k) => ({ x: c.x + Math.cos((k / 24) * Math.PI * 2) * r, y: c.y + Math.sin((k / 24) * Math.PI * 2) * r })), color, true);
  for (const pk of d.packs) {
    if (pk.path) {
      // Densify so the line follows the stair heights.
      const pts = pk.path.points.map((q) => q.pos);
      const ring = pk.path.mode === 'loop' ? [...pts, pts[0]!] : pts;
      const dense: Vec2[] = [];
      for (let i = 1; i < ring.length; i++) {
        const a = ring[i - 1]!;
        const b = ring[i]!;
        const n = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y)));
        for (let k = 0; k < n; k++) dense.push({ x: a.x + ((b.x - a.x) * k) / n, y: a.y + ((b.y - a.y) * k) / n });
      }
      dense.push(ring[ring.length - 1]!);
      line(dense, 0xffc94a, false);
      for (const q of pk.path.points) circle(q.pos, q.waitMs ? 1.2 : 0.5, 0xffc94a);
    }
    for (const sp of pk.spawns) if (sp.movement?.type === 'wander') circle(sp.pos, sp.movement.radius, 0x4ad8c8);
    if (pk.link && !pk.path) for (const sp of pk.spawns) circle(sp.pos, 1.4, pk.link === 'loose' ? 0x5aa0ff : 0xff6a5a);
  }
  return g;
}
